(() => {
  const AVAILABILITY_LABELS = {
    available: "Available",
    limited: "Limited availability",
    unavailable: "Unavailable",
    unknown: "Availability pending",
  };

  class LREBlankFinder {
    constructor(root) {
      this.root = root;
      this.apiBase = (root.dataset.apiBase || "").replace(/\/+$/, "");
      this.handoffUrl = root.dataset.handoffUrl || "";
      this.limit = Number(root.dataset.resultsLimit || 24);

      this.form = root.querySelector("[data-finder-form]");
      this.searchInput = root.querySelector("[data-finder-search]");
      this.brandSelect = root.querySelector("[data-finder-brand]");
      this.categorySelect = root.querySelector("[data-finder-category]");
      this.fitSelect = root.querySelector("[data-finder-fit]");
      this.colorSelect = root.querySelector("[data-finder-color]");
      this.sizeSelect = root.querySelector("[data-finder-size]");
      this.resetButton = root.querySelector("[data-finder-reset]");
      this.status = root.querySelector("[data-finder-status]");
      this.results = root.querySelector("[data-finder-results]");
      this.pagination = root.querySelector("[data-finder-pagination]");
      this.prevButton = root.querySelector("[data-finder-prev]");
      this.nextButton = root.querySelector("[data-finder-next]");
      this.pageLabel = root.querySelector("[data-finder-page-label]");
      this.dialog = root.querySelector("[data-finder-dialog]");
      this.dialogDetail = root.querySelector("[data-finder-detail]");
      this.dialogClose = root.querySelector("[data-finder-close]");
      this.basket = root.querySelector("[data-finder-basket]");
      this.basketItems = root.querySelector("[data-finder-basket-items]");
      this.basketCount = root.querySelector("[data-finder-basket-count]");
      this.clearBasketButton = root.querySelector("[data-finder-clear-basket]");
      this.continueButton = root.querySelector("[data-finder-continue]");

      this.selections = this.loadSelections();
      this.searchRequestId = 0;

      this.state = {
        query: "",
        brand: "",
        category: "",
        fit: "",
        color: "",
        size: "",
        page: 1,
        total: 0,
        activeStyle: null,
      };

      this.bindEvents();
      this.loadFilters();
    }

    bindEvents() {
      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.state.query = this.searchInput.value.trim();
        this.state.page = 1;
        this.runSearch();
      });

      this.brandSelect?.addEventListener("change", () => {
        this.state.brand = this.brandSelect.value;
        this.state.page = 1;
        if (this.hasSearchContext()) this.runSearch();
      });

      this.categorySelect?.addEventListener("change", () => {
        this.state.category = this.categorySelect.value;
        this.state.page = 1;
        if (this.hasSearchContext()) this.runSearch();
      });

      this.fitSelect?.addEventListener("change", () => {
        this.state.fit = this.fitSelect.value;
        this.state.page = 1;
        if (this.hasSearchContext()) this.runSearch();
      });

      this.colorSelect?.addEventListener("change", () => {
        this.state.color = this.colorSelect.value;
        this.state.page = 1;
        if (this.hasSearchContext()) this.runSearch();
      });

      this.sizeSelect?.addEventListener("change", () => {
        this.state.size = this.sizeSelect.value;
        this.state.page = 1;
        if (this.hasSearchContext()) this.runSearch();
      });

      this.resetButton?.addEventListener("click", () => this.reset());

      this.prevButton?.addEventListener("click", () => {
        if (this.state.page <= 1) return;
        this.state.page -= 1;
        this.runSearch({ preserveStatus: true });
      });

      this.nextButton?.addEventListener("click", () => {
        const maxPage = Math.max(1, Math.ceil(this.state.total / this.limit));
        if (this.state.page >= maxPage) return;
        this.state.page += 1;
        this.runSearch({ preserveStatus: true });
      });

      this.dialogClose?.addEventListener("click", () => this.closeDialog());

      this.dialog?.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });

      this.clearBasketButton?.addEventListener("click", () => {
        this.selections = [];
        this.saveSelections();
        this.renderBasket();
      });

      this.continueButton?.addEventListener("click", () => this.continueToRequest());

      this.renderBasket();
    }

    async loadFilters() {
      if (!this.apiBase) {
        this.setError("Blank Finder API URL is not configured.");
        return;
      }

      try {
        const [brands, categories] = await Promise.all([
          this.fetchJson("/api/brands"),
          this.fetchJson("/api/categories"),
        ]);

        this.populateSelect(this.brandSelect, brands, "All brands");
        this.populateSelect(this.categorySelect, categories, "All categories");
      } catch (error) {
        console.error("LRE Blank Finder filters failed", error);
        this.setStatus(
          "Search is available, but brand/category filters could not be loaded.",
        );
      }
    }

    populateSelect(select, items, defaultLabel) {
      if (!select || !Array.isArray(items)) return;

      const current = select.value;
      select.innerHTML = "";

      const defaultOption = document.createElement("option");
      defaultOption.value = "";
      defaultOption.textContent = defaultLabel;
      select.appendChild(defaultOption);

      items.forEach((item) => {
        if (!item || !item.name) return;
        const option = document.createElement("option");
        option.value = item.name;
        option.textContent = item.name;
        select.appendChild(option);
      });

      select.value = current;
    }

    async runSearch({ preserveStatus = false } = {}) {
      const requestId = ++this.searchRequestId;
      const query = this.searchInput.value.trim();

      this.state.query = query;
      this.state.brand = this.brandSelect.value;
      this.state.category = this.categorySelect.value;
      this.state.fit = this.fitSelect?.value || "";
      this.state.color = this.colorSelect?.value || "";
      this.state.size = this.sizeSelect?.value || "";

      if (!this.hasSearchContext()) {
        this.results.innerHTML = "";
        this.pagination.hidden = true;
        this.setStatus("Enter a garment search or choose a brand/category to browse.");
        return;
      }

      if (!preserveStatus) {
        this.setStatus("Searching blank apparel...");
      }

      this.results.setAttribute("aria-busy", "true");

      const params = new URLSearchParams({
        q: this.state.query,
        page: String(this.state.page),
        limit: String(this.limit),
      });

      if (this.state.brand) params.set("brand", this.state.brand);
      if (this.state.category) params.set("category", this.state.category);
      if (this.state.fit) params.set("fit", this.state.fit);
      if (this.state.color) params.set("color", this.state.color);
      if (this.state.size) params.set("size", this.state.size);

      try {
        const payload = await this.fetchJson(`/api/search?${params.toString()}`);

        if (requestId !== this.searchRequestId) return;

        const items = Array.isArray(payload.items) ? payload.items : [];

        this.state.total = Number.isFinite(Number(payload.total))
          ? Number(payload.total)
          : items.length;
        this.renderResults(items);
        this.renderPagination();

        if (items.length === 0) {
          this.setStatus("No garments matched your search.");
        } else {
          const first = (this.state.page - 1) * this.limit + 1;
          const last = first + items.length - 1;
          this.setStatus(
            `Showing ${first}-${last} of ${this.state.total} matching styles.`,
          );
        }
      } catch (error) {
        if (requestId !== this.searchRequestId) return;
        console.error("LRE Blank Finder search failed", error);
        this.results.innerHTML =
          '<div class="lre-blank-finder__error">We could not load blank apparel right now. Please try again.</div>';
        this.pagination.hidden = true;
        this.setStatus("Blank apparel search is temporarily unavailable.");
      } finally {
        this.results.removeAttribute("aria-busy");
      }
    }

    renderResults(items) {
      this.results.innerHTML = "";

      if (!items.length) {
        this.results.innerHTML =
          '<div class="lre-blank-finder__empty">Try another brand, style number, or garment description.</div>';
        return;
      }

      items.forEach((style) => {
        const card = document.createElement("article");
        card.className = "lre-blank-finder__card";

        const rawColorOptions = Array.isArray(style.colorOptions) ? style.colorOptions : [];
        const colorOptions = this.prioritizeColorOptions(
          rawColorOptions,
          this.state.color,
        );
        const preferredColorOption = this.state.color
          ? colorOptions.find((option) =>
              this.matchesColorFamily(option?.name, this.state.color),
            )
          : null;
        const image =
          this.safeImageUrl(preferredColorOption?.previewImage) ||
          this.safeImageUrl(style.imageUrls?.[0]);
        const brand = this.escapeHtml(style.brand?.name || "");
        const name = this.escapeHtml(style.name || "Blank garment");
        const styleNumber = this.escapeHtml(style.styleNumber || "");
        const styleId = this.escapeHtml(String(style.id || ""));

        card.innerHTML = `
          <div class="lre-blank-finder__card-media">
            ${
              image
                ? `<img src="${image}" alt="${name}" loading="lazy" width="600" height="600">`
                : '<div class="lre-blank-finder__card-placeholder">Image unavailable</div>'
            }
          </div>
          <div class="lre-blank-finder__card-body">
            <p class="lre-blank-finder__card-brand">${brand}</p>
            <h3 class="lre-blank-finder__card-title">${name}</h3>
            <p class="lre-blank-finder__card-style">Style ${styleNumber}</p>
            <span class="lre-blank-finder__badge lre-blank-finder__badge--unknown">
              Live availability
            </span>
            <div class="lre-blank-finder__card-actions">
              <button
                type="button"
                class="lre-blank-finder__button lre-blank-finder__button--primary"
                data-style-id="${styleId}"
              >
                View options
              </button>
            </div>
          </div>
        `;

        if (colorOptions.length) {
          const swatchRow = document.createElement("div");
          swatchRow.className = "lre-blank-finder__card-swatches";
          swatchRow.setAttribute("aria-label", "Available colors");

          const cardImage = card.querySelector(".lre-blank-finder__card-media img");
          const defaultImage = cardImage?.src || "";
          let selectedPreview =
            this.safeImageUrl(preferredColorOption?.previewImage) || "";

          colorOptions.slice(0, 6).forEach((option) => {
            const swatch = this.createSwatchElement(option, false);
            const previewImage = this.safeImageUrl(option?.previewImage);

            swatch.setAttribute("role", "button");
            swatch.setAttribute("tabindex", "0");
            swatch.setAttribute(
              "aria-label",
              `Preview ${option?.name || "color"}`,
            );

            if (
              this.state.color &&
              this.matchesColorFamily(option?.name, this.state.color)
            ) {
              swatch.classList.add("lre-blank-finder__swatch--selected");
            }

            const preview = () => {
              if (cardImage && previewImage) {
                cardImage.src = previewImage;
                cardImage.alt = `${name} - ${option?.name || "color"}`;
              }
            };

            const restore = () => {
              if (!cardImage) return;
              const image = selectedPreview || defaultImage;
              if (image) cardImage.src = image;
              cardImage.alt = name;
            };

            swatch.addEventListener("mouseenter", preview);
            swatch.addEventListener("focus", preview);
            swatch.addEventListener("mouseleave", restore);
            swatch.addEventListener("blur", restore);
            swatch.addEventListener("click", (event) => {
              event.preventDefault();

              selectedPreview = previewImage || selectedPreview;
              swatchRow
                .querySelectorAll(".lre-blank-finder__swatch--selected")
                .forEach((item) =>
                  item.classList.remove("lre-blank-finder__swatch--selected"),
                );

              swatch.classList.add("lre-blank-finder__swatch--selected");
              preview();
            });

            swatch.addEventListener("keydown", (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                swatch.click();
              }
            });

            swatchRow.appendChild(swatch);
          });

          if (colorOptions.length > 6) {
            const more = document.createElement("span");
            more.className = "lre-blank-finder__swatch-more";
            more.textContent = `+${colorOptions.length - 6}`;
            swatchRow.appendChild(more);
          }

          card.querySelector(".lre-blank-finder__card-actions")?.before(swatchRow);
        }

        card.querySelector("[data-style-id]")?.addEventListener("click", () => {
          this.openStyle(
            String(style.id),
            this.state.color,
            this.state.size,
          );
        });

        this.results.appendChild(card);
      });
    }

    renderPagination() {
      const maxPage = Math.max(1, Math.ceil(this.state.total / this.limit));
      const shouldShow = this.state.total > this.limit;

      this.pagination.hidden = !shouldShow;
      if (!shouldShow) return;

      this.prevButton.disabled = this.state.page <= 1;
      this.nextButton.disabled = this.state.page >= maxPage;
      this.pageLabel.textContent = `Page ${this.state.page} of ${maxPage}`;
    }

    async openStyle(styleId, preferredColorFilter = "", preferredSize = "") {
      this.openDialog();
      this.dialogDetail.innerHTML =
        '<div class="lre-blank-finder__empty">Loading garment options...</div>';

      try {
        const style = await this.fetchJson(
          `/api/styles/${encodeURIComponent(styleId)}`,
        );

        this.state.activeStyle = style;
        this.renderDetail(style, preferredColorFilter, preferredSize);
      } catch (error) {
        console.error("LRE Blank Finder style detail failed", error);
        this.dialogDetail.innerHTML =
          '<div class="lre-blank-finder__error">We could not load this garment. Please close this window and try again.</div>';
      }
    }

    renderDetail(style, preferredColorFilter = "", preferredSize = "") {
      const variants = Array.isArray(style.variants) ? style.variants : [];
      const colors = this.unique(
        variants.map((variant) => variant.color).filter(Boolean),
      );

      const image = this.safeImageUrl(style.imageUrls?.[0]);
      const brand = this.escapeHtml(style.brand?.name || "");
      const rawBrand = style.brand?.name || "";
      const rawName = style.name || "Blank garment";
      const displayName = this.compactProductName(rawName, rawBrand);
      const name = this.escapeHtml(displayName);
      const description = this.escapeHtml(this.cleanDescription(style.description || ""));

      this.dialogDetail.innerHTML = `
        <div class="lre-blank-finder__detail">
          <div class="lre-blank-finder__detail-image" data-detail-image>
            ${
              image
                ? `<img src="${image}" alt="${name}" width="800" height="800">`
                : '<div class="lre-blank-finder__card-placeholder">Image unavailable</div>'
            }
          </div>

          <div class="lre-blank-finder__detail-content">
            <p class="lre-blank-finder__detail-brand">${brand}</p>
            <h3 class="lre-blank-finder__detail-title">${name}</h3>
            ${
              description
                ? `<details class="lre-blank-finder__product-details" data-product-details>\n                    <summary>Product details</summary>\n                    <p class="lre-blank-finder__detail-description">${description}</p>\n                  </details>`
                : ""
            }

            <div class="lre-blank-finder__selector">
              <label for="LREBlankColor-${this.root.id}">Color</label>
              <select id="LREBlankColor-${this.root.id}" data-detail-color>
                <option value="">Choose a color</option>
                ${colors
                  .map(
                    (color) =>
                      `<option value="${this.escapeHtml(color)}">${this.escapeHtml(
                        color,
                      )}</option>`,
                  )
                  .join("")}
              </select>
            </div>

            <div class="lre-blank-finder__detail-swatches" data-detail-swatches></div>

            <div class="lre-blank-finder__selector">
              <label for="LREBlankSize-${this.root.id}">Size</label>
              <select id="LREBlankSize-${this.root.id}" data-detail-size disabled>
                <option value="">Choose a color first</option>
              </select>
            </div>

            <div class="lre-blank-finder__availability-row" data-detail-availability>
              <span class="lre-blank-finder__badge lre-blank-finder__badge--unknown">
                Select color and size
              </span>
            </div>
            <p class="lre-blank-finder__selection-note" data-detail-note>
              Stock may change before production.
            </p>

            <div class="lre-blank-finder__selector">
              <label for="LREBlankQty-${this.root.id}">Quantity</label>
              <input
                id="LREBlankQty-${this.root.id}"
                class="lre-blank-finder__input"
                type="number"
                min="1"
                step="1"
                value="1"
                inputmode="numeric"
                data-detail-quantity
              >
            </div>

            <button
              type="button"
              class="lre-blank-finder__button lre-blank-finder__button--primary"
              data-use-garment
              disabled
            >
              Add to Request
            </button>
          </div>
        </div>
      `;

      const productDetails = this.dialogDetail.querySelector("[data-product-details]");
      if (productDetails) {
        productDetails.open = false;
      }

      const colorSelect = this.dialogDetail.querySelector("[data-detail-color]");
      const sizeSelect = this.dialogDetail.querySelector("[data-detail-size]");
      const colorSwatches = this.dialogDetail.querySelector("[data-detail-swatches]");
      const availability = this.dialogDetail.querySelector(
        "[data-detail-availability]",
      );
      const useButton = this.dialogDetail.querySelector("[data-use-garment]");
      const detailImage = this.dialogDetail.querySelector("[data-detail-image]");
      let note = this.dialogDetail.querySelector("[data-detail-note]");
      const quantityInput = this.dialogDetail.querySelector("[data-detail-quantity]");

      let selectedVariant = null;

      const detailSwatches = [];

      if (colorSwatches) {
        colors.forEach((color) => {
          const variant = variants.find((item) => item.color === color);
          const swatch = this.createSwatchElement({
            name: color,
            swatch: variant?.images?.swatch,
            primaryHex: variant?.primaryHex,
            secondaryHex: variant?.secondaryHex,
          }, true);

          swatch.dataset.swatchColor = color;

          swatch.addEventListener("click", () => {
            if (swatch.dataset.unavailableForSize === "true") return;
            colorSelect.value = color;
            colorSelect.dispatchEvent(new Event("change", { bubbles: true }));
          });

          detailSwatches.push(swatch);
          colorSwatches.appendChild(swatch);
        });
      }

      const updateSwatchAvailability = () => {
        const selectedSize = sizeSelect.value;

        detailSwatches.forEach((swatch) => {
          const color = swatch.dataset.swatchColor || "";
          const matchingVariant = selectedSize
            ? variants.find(
                (variant) =>
                  variant.color === color &&
                  variant.size === selectedSize,
              )
            : null;

          const unavailable =
            Boolean(selectedSize) &&
            (!matchingVariant ||
              matchingVariant.availability === "unavailable");

          swatch.dataset.unavailableForSize = unavailable ? "true" : "false";
          swatch.classList.toggle(
            "lre-blank-finder__swatch--unavailable",
            unavailable,
          );
          swatch.setAttribute(
            "aria-disabled",
            unavailable ? "true" : "false",
          );

          const baseTitle = color || "Color";
          swatch.title = unavailable
            ? `${baseTitle} — unavailable in ${selectedSize}`
            : baseTitle;
        });
      };

      const updateSelection = () => {
        const color = colorSelect.value;
        const size = sizeSelect.value;

        selectedVariant =
          variants.find(
            (variant) => variant.color === color && variant.size === size,
          ) || null;

        if (!selectedVariant) {
          availability.innerHTML =
            '<span class="lre-blank-finder__badge lre-blank-finder__badge--unknown">Select color and size</span>';
          useButton.disabled = true;
          return;
        }

        const status = selectedVariant.availability || "unknown";
        const label = AVAILABILITY_LABELS[status] || AVAILABILITY_LABELS.unknown;

        availability.innerHTML = `
          <span class="lre-blank-finder__badge lre-blank-finder__badge--${this.escapeHtml(
            status,
          )}">
            ${this.escapeHtml(label)}
          </span>
        `;

        const variantImage = this.pickVariantImage(selectedVariant);
        if (variantImage) {
          detailImage.innerHTML = `<img src="${variantImage}" alt="${name} - ${this.escapeHtml(
            color,
          )}" width="800" height="800">`;
        }

        const hasHandoff = Boolean(this.handoffUrl);
        useButton.disabled = status === "unavailable" || !hasHandoff;

        if (!hasHandoff) {
          note.textContent =
            "Select a custom-order handoff page in the Shopify theme editor before publishing.";
        } else if (status === "unavailable") {
          note.textContent =
            "This color and size is currently unavailable. Choose another option.";
        } else if (status === "limited") {
          note.textContent =
            "Limited availability. LRE will confirm stock before production.";
        } else if (status === "unknown") {
          note.textContent =
            "Availability could not be confirmed automatically. LRE will verify stock before production.";
        } else {
          note.textContent =
            "Stock may change before production.";
        }
      };

      colorSelect.addEventListener("change", () => {
        const color = colorSelect.value;
        const previousSize = sizeSelect.value;
        const sizes = this.unique(
          variants
            .filter((variant) => variant.color === color)
            .map((variant) => variant.size)
            .filter(Boolean),
        );

        sizeSelect.innerHTML = color
          ? '<option value="">Choose a size</option>'
          : '<option value="">Choose a color first</option>';

        sizes.forEach((size) => {
          const option = document.createElement("option");
          option.value = size;
          option.textContent = size;
          sizeSelect.appendChild(option);
        });

        sizeSelect.disabled = !color;

        if (previousSize && sizes.includes(previousSize)) {
          sizeSelect.value = previousSize;
        }

        updateSwatchAvailability();
        updateSelection();
      });

      sizeSelect.addEventListener("change", () => {
        updateSwatchAvailability();
        updateSelection();
      });

      if (preferredColorFilter) {
        const preferredVariant = variants.find(
          (variant) =>
            this.matchesColorFamily(variant.color, preferredColorFilter) &&
            (!preferredSize || variant.size === preferredSize) &&
            variant.availability !== "unavailable",
        ) || variants.find(
          (variant) =>
            this.matchesColorFamily(variant.color, preferredColorFilter) &&
            variant.availability !== "unavailable",
        );

        if (preferredVariant) {
          colorSelect.value = preferredVariant.color;
          colorSelect.dispatchEvent(new Event("change", { bubbles: true }));

          if (
            preferredSize &&
            [...sizeSelect.options].some(
              (option) => option.value === preferredSize,
            )
          ) {
            sizeSelect.value = preferredSize;
            sizeSelect.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      }

      useButton.addEventListener("click", () => {
        if (!selectedVariant) return;

        const quantity = Math.max(
          1,
          Number.parseInt(quantityInput?.value || "1", 10) || 1,
        );

        this.addSelection(style, selectedVariant, quantity);
        note.textContent = "Added to your custom request. You can add another color, size, or garment.";
        useButton.textContent = "Added — Add Another";
      });
    }

    addSelection(style, variant, quantity) {
      const selection = {
        source: "lre-blank-apparel-finder",
        supplier: style.supplier || "ss-activewear",
        styleId: String(style.id || ""),
        styleNumber: style.styleNumber || "",
        styleName: style.name || "",
        brand: style.brand?.name || "",
        sku: variant.sku || "",
        color: variant.color || "",
        size: variant.size || "",
        availability: variant.availability || "unknown",
        image:
          this.pickVariantImage(variant) ||
          this.safeImageUrl(style.imageUrls?.[0]) ||
          "",
        garmentType: this.inferGarmentType(style),
        quantity,
      };

      const existingIndex = this.selections.findIndex(
        (item) => item.sku === selection.sku,
      );

      if (existingIndex >= 0) {
        this.selections[existingIndex] = selection;
      } else {
        this.selections.push(selection);
      }

      this.saveSelections();
      this.renderBasket();
    }

    inferGarmentType(style) {
      const haystack = [
        style.name,
        style.styleNumber,
        ...(Array.isArray(style.categories)
          ? style.categories.map((category) => category?.name)
          : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (/polo/.test(haystack)) return "Polo";
      if (/hoodie|sweatshirt|fleece|crewneck/.test(haystack)) return "Hoodie / Fleece";
      if (/jacket|outerwear|coat|windbreaker|vest/.test(haystack)) return "Jacket / Outerwear";
      if (/hat|cap|beanie|headwear/.test(haystack)) return "Hat / Headwear";
      if (/workwear|uniform/.test(haystack)) return "Workwear / Uniform";
      if (/jersey|performance|athletic|sport|team/.test(haystack)) return "Team / Performance Apparel";
      if (/bag|tote|backpack|accessor/.test(haystack)) return "Bag / Accessory";
      if (/t-shirt|t shirt|tee/.test(haystack)) return "T-Shirt";

      return "";
    }

    loadSelections() {
      try {
        const value = JSON.parse(
          sessionStorage.getItem("lreBlankApparelSelections") || "[]",
        );
        return Array.isArray(value) ? value : [];
      } catch {
        return [];
      }
    }

    saveSelections() {
      try {
        sessionStorage.setItem(
          "lreBlankApparelSelections",
          JSON.stringify(this.selections),
        );
      } catch (error) {
        console.warn("Unable to persist LRE blank selections", error);
      }
    }

    renderBasket() {
      if (!this.basket || !this.basketItems || !this.basketCount) return;

      const count = this.selections.length;
      this.basket.hidden = count === 0;
      this.basketCount.textContent = `${count} ${count === 1 ? "item" : "items"}`;
      this.basketItems.innerHTML = "";

      this.selections.forEach((selection, index) => {
        const item = document.createElement("div");
        item.className = "lre-blank-finder__basket-item";

        const image = this.safeImageUrl(selection.image);
        item.innerHTML = `
          <div class="lre-blank-finder__basket-thumb">
            ${
              image
                ? `<img src="${image}" alt="" loading="lazy" width="80" height="80">`
                : ""
            }
          </div>
          <div class="lre-blank-finder__basket-copy">
            <strong>${this.escapeHtml(selection.styleName || selection.styleNumber)}</strong>
            <span>${this.escapeHtml(
              [selection.brand, selection.styleNumber, selection.color, selection.size]
                .filter(Boolean)
                .join(" • "),
            )}</span>
            <span>Qty ${Math.max(1, Number(selection.quantity || 1))}</span>
          </div>
          <button type="button" class="lre-blank-finder__basket-remove" data-remove-index="${index}">
            Remove
          </button>
        `;

        item.querySelector("[data-remove-index]")?.addEventListener("click", () => {
          this.selections.splice(index, 1);
          this.saveSelections();
          this.renderBasket();
        });

        this.basketItems.appendChild(item);
      });
    }

    continueToRequest() {
      if (!this.handoffUrl || !this.selections.length) return;

      this.saveSelections();

      // Preserve the first selection in the legacy single-item handoff for
      // backwards compatibility while the request page consumes the full basket.
      const first = this.selections[0];
      try {
        sessionStorage.setItem(
          "lreBlankApparelSelection",
          JSON.stringify(first),
        );
      } catch (error) {
        console.warn("Unable to persist LRE blank selection", error);
      }

      const destination = new URL(this.handoffUrl, window.location.origin);
      destination.searchParams.set("lre_blank_source", "lre-blank-apparel-finder");
      destination.searchParams.set("lre_blank_count", String(this.selections.length));

      if (first.garmentType) {
        destination.searchParams.set("lre_blank_garment_type", first.garmentType);
      }

      window.location.assign(destination.toString());
    }

    pickVariantImage(variant) {
      const images = variant?.images || {};
      return (
        this.safeImageUrl(images.front) ||
        this.safeImageUrl(images.onModelFront) ||
        this.safeImageUrl(images.side) ||
        this.safeImageUrl(images.back) ||
        this.safeImageUrl(images.swatch) ||
        ""
      );
    }

    openDialog() {
      if (typeof this.dialog?.showModal === "function") {
        if (!this.dialog.open) this.dialog.showModal();
      } else if (this.dialog) {
        this.dialog.setAttribute("open", "");
      }
    }

    closeDialog() {
      if (typeof this.dialog?.close === "function") {
        this.dialog.close();
      } else {
        this.dialog?.removeAttribute("open");
      }
    }

    reset() {
      this.form?.reset();
      this.state = {
        query: "",
        brand: "",
        category: "",
        fit: "",
        color: "",
        size: "",
        page: 1,
        total: 0,
        activeStyle: null,
      };
      this.results.innerHTML = "";
      this.pagination.hidden = true;
      this.setStatus("Search by garment, or browse by brand/category. Refine by fit, color, and size when useful.");
      this.searchInput?.focus();
    }

    hasSearchContext() {
      return Boolean(
        this.searchInput?.value.trim() ||
        this.brandSelect?.value ||
        this.categorySelect?.value ||
        this.fitSelect?.value
      );
    }

    async fetchJson(path) {
      const response = await fetch(`${this.apiBase}${path}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(
          payload?.error?.message || "Blank Finder API request failed.",
        );
        error.code = payload?.error?.code;
        throw error;
      }

      return payload;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    setError(message) {
      this.setStatus(message);
      if (this.results) {
        this.results.innerHTML = `<div class="lre-blank-finder__error">${this.escapeHtml(
          message,
        )}</div>`;
      }
    }

    unique(values) {
      return [...new Set(values)];
    }

    cleanDescription(value) {
      if (!value) return "";

      const textarea = document.createElement("textarea");
      textarea.innerHTML = String(value);
      const decoded = textarea.value;

      const wrapper = document.createElement("div");
      wrapper.innerHTML = decoded;

      return (wrapper.textContent || wrapper.innerText || "")
        .replace(/\\/g, "")
        .replace(/\s+/g, " ")
        .trim();
    }

    prioritizeColorOptions(options, filter) {
      if (!filter) return [...options];

      return [...options].sort((a, b) => {
        const aMatch = this.matchesColorFamily(a?.name, filter) ? 1 : 0;
        const bMatch = this.matchesColorFamily(b?.name, filter) ? 1 : 0;
        return bMatch - aMatch;
      });
    }

    matchesColorFamily(colorName, filter) {
      if (!colorName || !filter) return false;

      const haystack = String(colorName).toLowerCase();
      const groups = {
        black: ["black"],
        white: ["white"],
        gray: ["gray", "grey", "charcoal", "silver"],
        red: ["red", "maroon", "burgundy", "cardinal"],
        blue: ["blue", "royal", "aqua", "teal"],
        navy: ["navy"],
        purple: ["purple", "violet", "lavender", "plum"],
        green: ["green", "olive", "forest", "lime"],
        "yellow-gold": ["yellow", "gold", "mustard"],
        orange: ["orange", "coral"],
        pink: ["pink", "rose", "fuchsia"],
        "brown-tan": ["brown", "tan", "khaki", "sand", "beige", "natural"],
        multi: ["multi", "tie dye", "camo", "print"],
      };

      const keywords = groups[filter] || [String(filter).toLowerCase()];
      return keywords.some((keyword) => haystack.includes(keyword));
    }

    compactProductName(name, brand) {
      const rawName = String(name || "").trim();
      const rawBrand = String(brand || "").trim();
      if (!rawName || !rawBrand) return rawName || "Blank garment";

      if (rawName.toLowerCase().startsWith(rawBrand.toLowerCase())) {
        const compact = rawName
          .slice(rawBrand.length)
          .replace(/^\s*[-—:]?\s*/, "")
          .trim();
        return compact || rawName;
      }

      return rawName;
    }

    createSwatchElement(option, interactive) {
      const element = document.createElement(interactive ? "button" : "span");
      element.className = interactive
        ? "lre-blank-finder__swatch lre-blank-finder__swatch--interactive"
        : "lre-blank-finder__swatch";

      if (interactive) element.type = "button";
      element.title = option?.name || "Color";
      if (interactive) element.setAttribute("aria-label", `Choose ${option?.name || "color"}`);

      const dot = document.createElement("span");
      dot.className = "lre-blank-finder__swatch-dot";

      const image = this.safeImageUrl(option?.swatch);
      const primary = /^#[0-9a-f]{6}$/i.test(option?.primaryHex || "")
        ? option.primaryHex
        : "#d9d9d9";
      const secondary = /^#[0-9a-f]{6}$/i.test(option?.secondaryHex || "")
        ? option.secondaryHex
        : "";

      if (image) {
        dot.style.backgroundImage = `url("${image}")`;
      } else if (secondary) {
        dot.style.background = `linear-gradient(90deg, ${primary} 50%, ${secondary} 50%)`;
      } else {
        dot.style.background = primary;
      }

      element.appendChild(dot);

      if (interactive) {
        const label = document.createElement("span");
        label.textContent = option?.name || "Color";
        element.appendChild(label);
      }

      return element;
    }

    safeImageUrl(value) {
      if (!value) return "";
      try {
        const url = new URL(value, window.location.origin);
        return url.protocol === "https:" ? url.href : "";
      } catch {
        return "";
      }
    }

    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }
  }

  function init(root = document) {
    root.querySelectorAll("[data-lre-blank-finder]").forEach((element) => {
      if (element.dataset.finderInitialized === "true") return;
      element.dataset.finderInitialized = "true";
      new LREBlankFinder(element);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });
})();
